/**
 * Floating "-N" damage numbers. Spawned at hit resolution, rise and fade
 * over ~1 second, then removed. DOM overlays projected to world coords
 * via the camera transform (same pattern as InventoryOverlay).
 */

interface DamageNumber {
  tileX: number;
  tileY: number;
  amount: number;
  /** 0..1 progression. Removed when >= 1. */
  progress: number;
  el: HTMLElement;
  crit: boolean;
  heal: boolean;
  /** Seconds this item lives (defaults to TTL_SEC). */
  ttl: number;
}

/** Seconds the number stays on screen. */
const TTL_SEC = 1.0;
/** Rise distance in device-independent pixels over TTL. */
const RISE_PX = 34;

export class DamageNumbers {
  readonly root: HTMLElement;
  private readonly active: DamageNumber[] = [];

  constructor(host: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'dmg-layer';
    host.appendChild(this.root);
  }

  /**
   * Spawn a floating number at a tile. Positive `amount` = damage (red -N,
   * crit-bump on kills); negative = healing (green +N, no crit variant).
   */
  spawn(tileX: number, tileY: number, amount: number, crit = false): void {
    if (amount === 0) return;
    const heal = amount < 0;
    const abs = Math.round(Math.abs(amount));
    const el = document.createElement('span');
    el.className = heal
      ? 'dmg-num dmg-heal'
      : crit
        ? 'dmg-num dmg-crit'
        : 'dmg-num';
    el.textContent = `${heal ? '+' : '-'}${abs}`;
    this.root.appendChild(el);
    this.active.push({ tileX, tileY, amount, progress: 0, el, crit, heal, ttl: TTL_SEC });
  }

  /** Spawn an arbitrary floating LABEL (e.g. "UNLOCKED!") at a tile — same
   *  rise-and-fade as a damage number, with a custom style class and lifetime. */
  spawnText(tileX: number, tileY: number, text: string, className = '', ttl = TTL_SEC): void {
    const el = document.createElement('span');
    el.className = `dmg-num ${className}`.trim();
    el.textContent = text;
    this.root.appendChild(el);
    this.active.push({ tileX, tileY, amount: 0, progress: 0, el, crit: false, heal: false, ttl });
  }

  /**
   * @param project world tile → screen pixels. Pass the renderer's real
   *   transform (`world.toGlobal`) so numbers track the tile through the
   *   camera's pivot/rotation/zoom — a plain axis-aligned projection is wrong
   *   once the world rotates.
   */
  update(dtSec: number, project: (tileX: number, tileY: number) => { x: number; y: number }): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const d = this.active[i]!;
      d.progress += dtSec / d.ttl;
      if (d.progress >= 1) {
        d.el.remove();
        this.active.splice(i, 1);
        continue;
      }
      const rise = -d.progress * RISE_PX;
      const alpha = 1 - d.progress * d.progress;
      const p = project(d.tileX, d.tileY);
      d.el.style.left = `${p.x}px`;
      d.el.style.top = `${p.y - 10 + rise}px`; // a touch above the tile, then rising
      d.el.style.opacity = alpha.toFixed(3);
    }
  }
}
